import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * This body previously sat in assets/component-quantity-selector.js — saved over
 * the base class rather than beside it. Two things broke at once: the base class
 * was destroyed (that file was left importing its own missing export, so it threw
 * and no selector anywhere defined), and this cart behaviour ended up under a
 * filename nothing loads for cart rows. Restoring it here matters because the
 * connectedCallback below is the ONLY thing that binds the cart steppers:
 * snippets/quantity-selector.liquid emits on:click routes just for product-form
 * rows (line_index == null), so cart rows have no declarative handler at all.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  #handleMinusClick = (event) => this.decreaseQuantity(event);
  #handlePlusClick = (event) => this.increaseQuantity(event);

  connectedCallback() {
    super.connectedCallback();

    // Cart drawer sections are replaced after every AJAX update. Bind directly
    // so the controls do not depend on the theme action router being rehydrated.
    if (this.dataset.roamTierSelector !== 'true') {
      this.refs.minusButton.addEventListener('click', this.#handleMinusClick);
      this.refs.plusButton.addEventListener('click', this.#handlePlusClick);
    }

    // Bundle selectors represent promotion tiers, not the Shopify variant's
    // inventory quantity. A stale variant limit such as max="0" makes the
    // browser reject every value because the input still has min="1".
    const { min, max } = this.getCurrentValues();
    if (this.dataset.roamTierSelector === 'true' || (max !== null && max < min)) {
      this.refs.quantityInput.removeAttribute('max');
      this.updateButtonStates();
    }
  }

  disconnectedCallback() {
    this.refs.minusButton?.removeEventListener('click', this.#handleMinusClick);
    this.refs.plusButton?.removeEventListener('click', this.#handlePlusClick);
    super.disconnectedCallback();
  }

  /**
   * Ignores variant maximums when constraints are refreshed for a tier selector.
   * @param {string} min
   * @param {string|null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    super.updateConstraints(min, this.dataset.roamTierSelector === 'true' ? null : max, step);
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    if (this.dataset.roamTierSelector === 'true') return null;

    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, max, value } = this.getCurrentValues();

    // Never leave the native number input in an impossible min/max state.
    if (max !== null && max < min) {
      this.refs.quantityInput.removeAttribute('max');
    }

    const effectiveMax = this.getEffectiveMax();
    const tierPairCount = Number(this.dataset.roamTierPairCount || 0);

    if (this.dataset.roamTierSelector === 'true' && tierPairCount > 0) {
      this.refs.quantityInput.removeAttribute('max');
      minusButton.disabled = tierPairCount <= 1;
      plusButton.disabled = false;
      return;
    }

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
